import React from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";

const CARDS = [
  {
    n: "01",
    title: "Your AI stays with you",
    body:
      "Your own AI, on systems you control. In your region, under your rules. Your data never leaves your walls.",
  },
  {
    n: "02",
    title: "A plan you can act on",
    body:
      "The plan tells you what to do first and what can wait. It ranks your opportunities, names what is holding them back, and ends with a ninety-day plan. Most firms are sitting on AI opportunities nobody has seen. This is how you become the one who sees them first.",
  },
  {
    n: "03",
    title: "Made for your country's rules",
    body:
      "If your firm is regulated, your plan checks you against your own regulator. Nothing generic. Nothing copied from another country.",
  },
];

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="cw-assessment cw-landing" data-testid="landing-page">
      <Header variant="forest" />
      <section className="cw-landing-shell">
        <div className="cw-landing-back" data-testid="landing-back-wrap">
          <button
            type="button"
            onClick={() => nav("/")}
            className="cw-back-btn"
            data-testid="landing-back-btn"
            aria-label="Back to Crowther home"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M19 12H5M11 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Back</span>
          </button>
        </div>
        <div className="cw-landing-hero">
          <div className="cw-landing-lead cw-fade-in">
            <div className="cw-eyebrow" data-testid="landing-eyebrow">
              Human Insight, Amplified.
            </div>
            <h1 className="cw-display-xl" style={{ marginTop: 28 }} data-testid="landing-headline">
              Where in your organisation can AI <em style={{ fontStyle: "italic", color: "var(--cw-emerald)" }}>make you money</em>, <em style={{ fontStyle: "italic", color: "var(--cw-emerald)" }}>save you money</em>, and where should it go first?
            </h1>
            <p
              className="cw-body cw-landing-lede"
              style={{ marginTop: 32, color: "rgba(22,30,27,0.82)" }}
              data-testid="landing-body"
            >
              Firms in your sector are already moving on AI. Most are guessing. In five minutes, you will not be. The Crowther AI Plan is a free report, made for your firm. It shows you where AI can grow your revenue, where it can cut your costs, and what to start now. You get a clear list of your best opportunities and a ninety-day plan you can take to your team. Thirteen questions. Five minutes. Your plan is in your inbox the moment you finish.
            </p>
            <div className="cw-landing-actions">
              <button
                className="cw-btn cw-btn-primary"
                data-testid="landing-start-btn"
                onClick={() => nav("/plan/gate")}
              >
                Start now
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <a
                className="cw-btn cw-btn-secondary"
                href="https://crowtherlabs.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="landing-learn-btn"
              >
                Learn about Crowther
              </a>
            </div>
          </div>

          <aside className="cw-landing-side cw-fade-in" style={{ animationDelay: "160ms" }} aria-label="Sovereignty principle">
            <blockquote data-testid="landing-quote">
              Your data lives in a private place. The AI built on it should live there too.
            </blockquote>
            <div className="cw-small">
              This idea is now part of data law in a growing number of countries.
            </div>
            <div
              style={{
                marginTop: 40,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 20,
              }}
              className="cw-stagger"
            >
              <div>
                <div className="cw-display-l" style={{ color: "var(--cw-emerald)" }} data-testid="stat-questions">13</div>
                <div className="cw-small" style={{ marginTop: 4 }}>Simple questions</div>
              </div>
              <div>
                <div className="cw-display-l" style={{ color: "var(--cw-emerald)" }} data-testid="stat-minutes">5<span style={{ fontSize: "0.5em", marginLeft: 4, letterSpacing: 0 }}>min</span></div>
                <div className="cw-small" style={{ marginTop: 4 }}>From start to answers</div>
              </div>
              <div>
                <div className="cw-display-l" style={{ color: "var(--cw-emerald)", fontSize: "clamp(28px, 4vw, 44px)" }} data-testid="stat-inbox">Instant</div>
                <div className="cw-small" style={{ marginTop: 4 }}>In your inbox the moment you finish</div>
              </div>
              <div>
                <div className="cw-display-l" style={{ color: "var(--cw-marigold)" }} data-testid="stat-free">Free</div>
                <div className="cw-small" style={{ marginTop: 4 }}>Nothing to pay, nothing owed</div>
              </div>
            </div>
          </aside>
        </div>

        <div className="cw-landing-cards cw-stagger">
          {CARDS.map((c) => (
            <article key={c.n} className="cw-card" data-testid={`landing-card-${c.n}`}>
              <div className="cw-landing-card-number">{c.n}</div>
              <hr className="cw-rule" style={{ marginTop: 20 }} />
              <div className="cw-heading-m">{c.title}</div>
              <p className="cw-body" style={{ marginTop: 12, color: "rgba(22,30,27,0.78)" }}>
                {c.body}
              </p>
            </article>
          ))}
        </div>

        <div className="cw-landing-closer cw-fade-in" data-testid="landing-closer">
          <p className="cw-landing-closer-line">
            Five minutes from now, you will know something about your firm that nobody else in it knows yet. Every week you wait, that head start belongs to someone else.
          </p>
          <button
            className="cw-btn cw-btn-primary cw-landing-closer-cta"
            data-testid="landing-closer-cta"
            onClick={() => nav("/plan/gate")}
          >
            Free today: your firm&rsquo;s customised AI Strategy Report
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <footer style={{ marginTop: 96, paddingTop: 32, borderTop: "1px solid var(--cw-border-dark)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
            <div className="cw-small">Crowther &middot; crowtherlabs.com</div>
            <div className="cw-small">Private &middot; Free of charge</div>
          </div>
        </footer>
      </section>
    </div>
  );
}
